import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination, Scrollbar, Thumbs, FreeMode } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/thumbs';

/**
 * Slider web component
 */
class AtSlider extends HTMLElement {
  static swiperInstances = new Map();

  /**
   * Constructor
   */
  constructor() {
    super();
    this.id = this.id || this.generateId();
    this.sliderName = this.getAttribute('slider-name') || '.swiper';
    this.slidesPerView = this.getAttribute('slides-per-view') === 'auto' ? 'auto' : parseFloat(this.getAttribute('slides-per-view'), 10) || 1;
    this.slidesPerGroup = parseInt(this.getAttribute('slides-per-group'), 10) || 1;
    this.spaceBetween = parseInt(this.getAttribute('space-between'), 10) || 0;
    this.effect = this.getAttribute('effect') || 'slide';
    this.loop = this.hasAttribute('loop');
    this.allowTouchMove = this.getAttribute('allow-touch-move') === 'false' ? false : true;
    this.speed = parseInt(this.getAttribute('speed'), 10) || 1000;
    this.centeredSlides = this.hasAttribute('centered-slides');
    this.initialSlide = parseInt(this.getAttribute('initial-slide'), 10) || 0;
    this.freeMode = this.hasAttribute('free-mode');
    this.freeModeSticky = this.getAttribute('free-mode-sticky') === 'true' ? true : false;
    this.pagination = this.hasAttribute('pagination');
    this.paginationElement = this.getAttribute('pagination-element') || '.swiper-pagination';
    this.paginationClickable = this.hasAttribute('pagination-clickable');
    this.scrollBar = this.hasAttribute('scroll-bar');
    this.scrollBarElement = this.getAttribute('scrollbar-element') || '.swiper-scrollbar';
    this.autoplay = this.hasAttribute('autoplay');
    this.autoplayDelay = Number.isNaN(parseInt(this.getAttribute('autoplay-delay'), 10)) ? 3000 : parseInt(this.getAttribute('autoplay-delay'), 10);
    this.navigation = this.hasAttribute('navigation');
    this.navigationElementNext = this.getAttribute('navigation-element-next') || '.swiper-button-next';
    this.navigationElementPrev = this.getAttribute('navigation-element-prev') || '.swiper-button-prev';
    this.breakpoints = this.hasAttribute('breakpoints');
    this.direction = this.getAttribute('direction') || 'horizontal';
    this.watchSlidesProgress = this.hasAttribute('watch-slides-progress');
    this.watchOverflow = this.hasAttribute('watch-overflow');
    this.thumbsId = this.getAttribute('thumbs-id') || null;
    const bp = this.getAttribute('breakpoints-settings');
    this.breakpointsSettings = bp
      ? JSON.parse(bp)
      : {
          1024: { slidesPerView: this.slidesPerView, spaceBetween: this.spaceBetween },
          768: { slidesPerView: this.slidesPerView, spaceBetween: this.spaceBetween },
          375: { slidesPerView: this.slidesPerView, spaceBetween: this.spaceBetween },
        };
    this.swiperInstance = null;
  }

  /**
   * Add string characters
   *
   * @param {number} dec string or number
   * @returns {string} hex
   */
  dec2hex(dec) {
    return dec.toString().padStart(2, '0');
  }

  /**
   * Generate id
   *
   * @returns {string} string
   */
  generateId() {
    const arr = new Uint8Array(14);
    window.crypto.getRandomValues(arr);
    return Array.from(arr, this.dec2hex).join('');
  }

  /**
   * Returns swiper modules
   *
   * @returns {Array} swiper modules
   */
  getSwiperModules() {
    return [
      ...(this.autoplay ? [Autoplay] : []),
      ...(this.navigation ? [Navigation] : []),
      ...(this.pagination ? [Pagination] : []),
      ...(this.scrollBar ? [Scrollbar] : []),
      ...(this.thumbsId ? [Thumbs] : []),
      ...(this.freeMode ? [FreeMode] : []),
    ];
  }

  /**
   * Returns swiper options
   *
   * @returns {object} swiper options
   */
  getSwiperOptions() {
    return {
      slidesPerView: this.slidesPerView,
      slidesPerGroup: this.slidesPerGroup,
      spaceBetween: this.spaceBetween,
      effect: this.effect,
      loop: this.loop,
      speed: this.speed,
      centeredSlides: this.centeredSlides,
      initialSlide: this.initialSlide,
      direction: this.direction,
      watchSlidesProgress: this.watchSlidesProgress,
      ...(this.watchOverflow && { watchOverflow: true }),
      ...(this.pagination && {
        pagination: {
          el: this.querySelector(this.paginationElement),
          clickable: this.paginationClickable,
        },
      }),
      ...(this.scrollBar && {
        scrollbar: {
          el: this.querySelector(this.scrollBarElement),
        },
      }),
      ...(this.autoplay && {
        autoplay: {
          delay: this.autoplayDelay,
        },
      }),
      ...(this.navigation && {
        navigation: {
          nextEl: this.querySelector(this.navigationElementNext),
          prevEl: this.querySelector(this.navigationElementPrev),
        },
      }),
      ...(this.breakpoints && {
        breakpoints: this.breakpointsSettings,
      }),
      ...(this.thumbsId && {
        thumbs: {
          swiper: this.constructor.swiperInstances.get(this.thumbsId),
        },
      }),
      ...(this.freeMode && {
        freeMode: {
          enabled: true,
          minimumVelocity: 0.02,
          momentumBounce: true,
          momentumRatio: window.customVariables.momentum_ratio,
          momentumVelocityRatio: window.customVariables.momentum_velocity_ratio,
          sticky: this.freeModeSticky,
        },
      }),
      allowTouchMove: this.allowTouchMove
    };
  }

  /**
   * init
   */
  initSwiper() {
    try {
      const swiperEl = this.querySelector(this.sliderName);
      if (!swiperEl) return;
      if (!this.swiperInstance) {
        this.swiperInstance = new Swiper(swiperEl, {
          ...this.getSwiperOptions(),
          modules: this.getSwiperModules(),
        });
        if (this.watchOverflow) {
          const updateLocked = () => {
            const isLocked = this.swiperInstance.isLocked;
            const containerWidth = swiperEl.offsetWidth;
            const wrapperWidth = this.swiperInstance.wrapperEl?.scrollWidth;
            const slidesTotal = Array.from(this.swiperInstance.slides).reduce((acc, slide) => acc + slide.offsetWidth, 0);
            console.log('[at-slider watchOverflow]', {
              isLocked,
              containerWidth,
              wrapperScrollWidth: wrapperWidth,
              slidesTotalWidth: slidesTotal,
              spaceBetween: this.swiperInstance.params.spaceBetween,
            });
            swiperEl.classList.toggle('swiper-content-fits', isLocked);
          };
          this.swiperInstance.on('lock', updateLocked);
          this.swiperInstance.on('unlock', updateLocked);
          updateLocked();
        }
      }
    } catch (e) {
      // Silent fail
    }
  }

  /**
   * connectedCallback
   */
  connectedCallback() {
    this.initSwiper();
    this.constructor.swiperInstances.set(this.id, this.swiperInstance);
  }

  /**
   * disconnectedCallback
   */
  disconnectedCallback() {
    this.constructor.swiperInstances.delete(this.id);
  }
}

customElements.define('at-slider', AtSlider);
